import BemClass from '@upsales/components/Utils/bemClass';
import React, { useEffect, useState } from 'react';
import { Block, Headline, Input, Logo, Text } from '@upsales/components';
import { DefaultButton } from '@upsales/components/Buttons';
import { SlideFade } from '@upsales/components/animations';
import LoginError, { LoginErrors } from 'App/pages/Login/LoginError';
import SessionResource from 'App/resources/Session';
import axios from 'axios';
import config from 'App/babel/config';
import './MCPLogin.scss';

type State = 'idle' | 'ssoRedirect' | 'twoFA' | 'consent' | 'completing' | 'done' | 'error';

type SessionInfo = {
	clientName: string;
	redirectUri: string;
};

function getSessionParam(): string | null {
	const hash = window.location.hash;
	const idx = hash.indexOf('?');
	if (idx === -1) return null;
	const params = new URLSearchParams(hash.slice(idx));
	return params.get('session');
}

function getRedirectDomain(uri: string): string {
	try {
		const parsed = new URL(uri);
		return parsed.host;
	} catch {
		return uri;
	}
}

export default function MCPLogin() {
	const classes = new BemClass('MCPLogin');
	const sessionId = getSessionParam();

	const [state, setState] = useState<State>('idle');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [twoFaToken, setTwoFaToken] = useState('');
	const [twoFaCode, setTwoFaCode] = useState('');
	const [error, setError] = useState<LoginErrors | null>(null);
	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);

	const completeAuth = async () => {
		setState('completing');
		try {
			const { data } = await axios.post(`${config.URL}${config.API}mcp/authorize/callback`, {
				session: sessionId
			});
			const result = data.data;
			const redirectUrl = `${result.redirect_uri}${
				result.redirect_uri.includes('?') ? '&' : '?'
			}code=${encodeURIComponent(result.code)}&state=${encodeURIComponent(result.state)}`;
			window.location.href = redirectUrl;
			setState('done');
		} catch (err: any) {
			const status = err.response?.status;
			if (status === 403) {
				setError(LoginErrors.MCP_ACCESS_DENIED);
			} else {
				setError(LoginErrors.UNKNOWN);
			}
			setState('error');
		}
	};

	// On mount: fetch session info and check if already authenticated
	useEffect(() => {
		if (!sessionId) {
			setError(LoginErrors.UNKNOWN);
			setState('error');
			return;
		}

		// Fetch session info to show on consent screen
		axios
			.get(`${config.URL}${config.API}mcp/session/${sessionId}`)
			.then(res => {
				setSessionInfo({
					clientName: res.data.client_name,
					redirectUri: res.data.redirect_uri
				});
			})
			.catch(() => {
				setError(LoginErrors.UNKNOWN);
				setState('error');
			});

		// Check if already authenticated (e.g., returning from SAML)
		axios
			.get(`${config.URL}${config.API}self`)
			.then(() => {
				// Already authenticated — show consent screen
				setState('consent');
			})
			.catch(() => {
				// Not authenticated — show login form
				setState('idle');
			});
	}, []);

	const checkLoginMethod = async () => {
		if (!email) return;
		setLoading(true);
		try {
			const { data } = await axios.post(`${config.URL}${config.API}loginMethod`, {
				email,
				mcpSession: sessionId
			});
			if (data.method === 'saml') {
				setState('ssoRedirect');
				window.location.href = data.url;
			} else {
				setShowPassword(true);
			}
		} catch {
			setShowPassword(true);
		} finally {
			setLoading(false);
		}
	};

	const doLogin = async () => {
		if (!email || !password) return;
		setLoading(true);
		setError(null);

		try {
			const { data } = await SessionResource.login({ email, password });
			if (data.isTwoFactorAuth) {
				setTwoFaToken(data.token);
				setState('twoFA');
				setLoading(false);
				return;
			}
			// Show consent screen instead of auto-completing
			setState('consent');
		} catch (err: any) {
			const res = err.response?.data;
			const status = err.response?.status;
			if (status === 403 && res?.errorCode === 140) {
				setError(LoginErrors.ACCOUNT_LOCKED);
			} else if (status === 403) {
				setError(LoginErrors.IP_BLOCKED);
			} else if (status === 402) {
				setError(res?.key === 'TrialEnded' ? LoginErrors.TRIAL_EXPIRED : LoginErrors.ACCOUNT_EXPIRED);
			} else if (res?.errorCode === 116) {
				setError(LoginErrors.FORCE_RESET_PASSWORD);
			} else if (res?.errorCode === 171) {
				setError(LoginErrors.USER_INACTIVE);
			} else {
				setError(LoginErrors.CREDENTIALS);
			}
			setLoading(false);
		}
	};

	const doTwoFA = async () => {
		if (!twoFaCode) return;
		setLoading(true);
		setError(null);

		try {
			await SessionResource.loginTwoFA(twoFaToken, twoFaCode);
			// Show consent screen instead of auto-completing
			setState('consent');
			setLoading(false);
		} catch (err: any) {
			const res = err.response?.data;
			if (res?.key === 'TwoFactorCodeWrong') {
				setError(LoginErrors.INVALID_2FA);
			} else if (res?.key === 'TwoFactorCodeDisabled') {
				setError(LoginErrors.OUTDATED_2FA);
			} else {
				setError(LoginErrors.UNKNOWN);
			}
			setLoading(false);
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (showPassword) {
			doLogin();
		} else {
			checkLoginMethod();
		}
	};

	const renderContent = () => {
		if (state === 'completing' || state === 'done') {
			return (
				<Block space="ptxl">
					<Text color="super-light-green" size="lg">
						{'Authorizing...'}
					</Text>
				</Block>
			);
		}

		if (state === 'ssoRedirect') {
			return (
				<Block space="ptxl">
					<Text color="super-light-green" size="lg">
						{'Redirecting to your identity provider...'}
					</Text>
				</Block>
			);
		}

		if (state === 'consent') {
			const domain = sessionInfo ? getRedirectDomain(sessionInfo.redirectUri) : '';
			return (
				<div>
					<Headline size="md" color="super-light-green">
						{'Authorize access'}
					</Headline>
					<Block space="mtxl mbl">
						<Text color="super-light-green" size="lg">
							<strong>{sessionInfo?.clientName || 'An application'}</strong>
							{' wants access to your Upsales account.'}
						</Text>
					</Block>
					<Block space="mbl" backgroundColor="rgba(255,255,255,0.05)" borderRadius>
						<Block space="pal">
							<Text color="grey-10" size="sm">
								{'Redirect destination:'}
							</Text>
							<Text color="super-light-green" bold>
								{domain}
							</Text>
						</Block>
					</Block>
					<Block space="mtxl">
						<DefaultButton block size="xl" onClick={completeAuth}>
							{'Allow access'}
						</DefaultButton>
					</Block>
					<Block space="mtm">
						<DefaultButton block size="xl" type="lined" onClick={() => window.close()}>
							{'Deny'}
						</DefaultButton>
					</Block>
				</div>
			);
		}

		if (state === 'twoFA') {
			return (
				<form
					onSubmit={e => {
						e.preventDefault();
						doTwoFA();
					}}
				>
					<Headline size="md" color="super-light-green">
						{'Two-factor authentication'}
					</Headline>
					<Block space="mtxl mbl">
						<Input
							size="lg"
							placeholder="Verification code"
							value={twoFaCode}
							disabled={loading}
							onChange={e => setTwoFaCode(e.target.value)}
							autoFocus
						/>
					</Block>
					{error ? <LoginError error={error} onClear={() => setError(null)} /> : null}
					<Block space="mtl">
						<DefaultButton submit block size="xl" loading={loading} disabled={!twoFaCode}>
							{'Verify'}
						</DefaultButton>
					</Block>
				</form>
			);
		}

		return (
			<form onSubmit={handleSubmit}>
				<Headline size="md" color="super-light-green">
					{'Sign in to authorize MCP access'}
				</Headline>
				{sessionInfo ? (
					<Block space="mtm mbl">
						<Text color="grey-10" size="sm">
							<strong>{sessionInfo.clientName}</strong>
							{` (${getRedirectDomain(sessionInfo.redirectUri)})`}
						</Text>
					</Block>
				) : null}
				<Block space="mtxl mbl">
					<Input
						size="lg"
						placeholder="Email"
						value={email}
						disabled={loading}
						onChange={e => setEmail(e.target.value)}
						onBlur={checkLoginMethod}
						autoFocus
					/>
				</Block>
				<SlideFade visible={showPassword} direction="top" height>
					<Block space="mbl">
						<Input
							size="lg"
							type="password"
							placeholder="Password"
							value={password}
							disabled={loading}
							onChange={e => setPassword(e.target.value)}
						/>
					</Block>
				</SlideFade>
				{error ? <LoginError error={error} onClear={() => setError(null)} /> : null}
				<Block space="mtl">
					<DefaultButton
						submit
						block
						size="xl"
						loading={loading}
						disabled={!email || (showPassword && !password)}
					>
						{showPassword ? 'Sign in' : 'Continue'}
					</DefaultButton>
				</Block>
			</form>
		);
	};

	return (
		<div className={classes.b()}>
			<div className={classes.elem('card').b()}>
				<div className={classes.elem('logo').b()}>
					<Logo color="super-light-green" />
				</div>
				{renderContent()}
			</div>
		</div>
	);
}
